


import { Component, Vue, Watch } from 'vue-property-decorator';
import LoadingAnimationComponent from '@/vue/components/LoadingAnimationComponent.vue';
import PasswordTextComponent from '@/vue/components/PasswordTextComponent.vue';

import escapeStringRegexp from 'escape-string-regexp';

@Component(
	{
		components:
		{
			LoadingAnimationComponent,
			PasswordTextComponent,
		},
	},
)
export default class PasswordResetView extends Vue
{
	// TODO: 最大長はパスワードのハッシュ化を実装したら100文字にする
	// ユーザが入力可能なパスワード文字列仕様の検討（2019版）Issue #284
	// - https://github.com/psofthouse/cgi-bin-pproducts_dev/issues/284
	private maxLength: number = 32;
	private minLength: number = 8;
	// パスワードの正規表現(半角英数字・記号)
	// MEMO: abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!"#$%&'()*+,-./:;<=>?@[]^_`{|}~
	private regex = '^[\\x21-\\x5B\\x5D-\\x7E]*$';

	private errorMessage: string         = '';
	private errorCode: number            = 0;
	private errors: string[]             = [];
	private sid: string                  = '';
	private passwords: string[]          = ['', ''];
	private patterns: string[]           = [];
	private isDisabledForSubmit: boolean = true;

	private async created()
	{
		this.isDisabledForSubmit = true;

		// パターン初期化
		this.patterns = [ this.regex, '^$' ];

		this.isDisabledForSubmit = false;
	}

	@Watch('passwords')
	private onPasswordChanged()
	{
		// MEMO: escape-string-regexp モジュールの改修待ち
		// https://github.com/sindresorhus/escape-string-regexp/issues/9#issuecomment-527545784
		const regex      = escapeStringRegexp(this.passwords[0]);
		// ハイフンをエスケープすると HTML に怒られるので当分の間は置換で対応
		this.patterns[1] = '^' + regex.replace(/\\-/g, '-') + '$';

		// パスワードのバリデーション
		this.verifyPassword(this.passwords[0], this.passwords[1]);
	}

	// サブミットボタンクリックイベント
	private onSubmitButtonClicked(event: MouseEvent)
	{
		const target = event.target as HTMLButtonElement;
		const focus  = document.activeElement as HTMLElement;

		// フォーカスが null もしくは BODY オブジェクトの場合(MacOS システムでの動作対策)
		if ( ! focus || focus.tagName === 'BODY' )
		{
			// スルー
			return;
		}
		// 他の要素からのサブミットイベントが発生した場合
		else if (target.type === 'submit'
			&& focus !== target)
		{
			// キャンセル
			event.preventDefault();
			return;
		}

		// パスワードのバリデーション
		this.verifyPassword(this.passwords[0], this.passwords[1]);
	}

	private async onSubmit()
	{
		// ボタンを押せないようにする
		this.isDisabledForSubmit = true;

		// 新パスワードを送信する
		if ( ! await this.sendPasswordToAPI(this.passwords[0]) )
		{
			switch (this.errorCode)
			{
				// リクエストパラメータ関係のエラー
				// MEMO: ブラウザから実行する場合、フロントエンド側で正しい実装をしていれば絶対に通らないはず
				case 2201300:
				case 2201301:
				case 2201302:
					this.$router.replace(
						{
							name: 'error',
							query:
							{
								code: this.errorCode.toString(),
								msgId: 'param_error',
							},
						},
					);
					return;

				// セッション関係のエラー
				case 2201303:
					this.$router.replace(
						{
							name: 'error',
							query:
							{
								code: this.errorCode.toString(),
								msgId: 'sid_expired',
							},
						},
					);
					return;

				// パスワードバリデーションエラー
				// MEMO: クライアント側でバリデーションしたものを送信するため、絶対に通らないはず
				case 2201304:
					this.$router.replace(
						{
							name: 'error',
							query:
							{
								code: this.errorCode.toString(),
								msgId: 'failed_validation_password',
							},
						},
					);
					return;

				// データベースアップデート失敗
				case 2201305:
					this.$router.replace(
						{
							name: 'error',
							query:
							{
								code: this.errorCode.toString(),
								msgId: 'failed_password_update',
							},
						},
					);
					return;

				// サーバー関連のエラー
				case 2200305:
				case 2201306:
				case 2201307:
					this.$router.replace(
						{
							name: 'error',
							query:
							{
								code: this.errorCode.toString(),
								msgId: 'server_error',
							},
						},
					);
					return;
			}

			this.isDisabledForSubmit = false;
			return;
		}

		// 現在の画面をセッションエラー表示に置き換える(ヒストリバック用)
		this.$router.replace(
			{
				name: 'error',
				query: { msgId: 'sid_expired' },
			},
		);

		// パスワード再設定完了ページへ
		this.$router.push({ name: 'password_reset_completed'});
	}

	private verifyPassword(p1: string, p2: string): boolean
	{
		const elemPw0: any = (this.$refs.pw0 as any)[0].$refs.input;
		const elemPw1: any = (this.$refs.pw1 as any)[0].$refs.input;
		const length: number = p1.length;
		let msg: string;

		// パスワードフィールドが空
		if ( length === 0 )
		{
			msg = this.$t('password_reset.errors.empty') as string;
			elemPw0.setCustomValidity(msg);
			return false;
		}
		// パスワードが長い
		else if ( length > this.maxLength )
		{
			msg = this.$t('password_reset.errors.too_long') as string;
			elemPw0.setCustomValidity(msg);
			return false;
		}
		// パスワードが短い
		else if ( length < this.minLength )
		{
			msg = this.$t('password_reset.errors.too_short') as string;
			elemPw0.setCustomValidity(msg);
			return false;
		}
		// 使用不可能な文字を使用している
		else if ( ! p1.match(this.regex) )
		{
			msg = this.$t('password_reset.errors.limited_char') as string;
			elemPw0.setCustomValidity(msg);
			return false;
		}
		elemPw0.setCustomValidity('');

		// 同一チェック
		if ( p1 !== p2 )
		{
			msg = this.$t('password_reset.errors.no_match') as string;
			elemPw1.setCustomValidity(msg);
			return false;
		}

		elemPw1.setCustomValidity('');
		return true;
	}

	private async sendPasswordToAPI(password: string): Promise<boolean>
	{
		let result: boolean = false;
		await this.$http.post(
			this.$store.state.apiPath + 'do_reset',
			{
				new_passwd: password,
			},
		)
		.then( (res) =>
			{
				result = res.data.status ? true : false;
				this.errorCode = res.data.error_code;
			},
		)
		.catch( (err) =>
			{
				// サーバーからのエラーレスポンス
				if ( err.response )
				{
					result = err.response.data.status ? true : false;
					this.errorCode = err.response.data.error_code;
					return;
				}

				// サーバーからのレスポンスがない場合はタイムアウトと見做す
				this.$router.replace(
					{
						name: 'error',
						query:
						{
							code: '2200999',
							msgId: 'timed_out',
						},
					},
				);
			},
		);
		return result;
	}
}
